import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';
import backend from 'utils/backend';

import {Box, Stack} from 'views/components';
import {DeleteConfirmDialog} from 'views/module';

import RoutinesList from './routine_view/RoutinesList';
import RoutinesView from './routine_view/RoutinesView';
import RoutinesEdit from './routine_view/RoutinesEdit';

const RoutinesScene = (props) => {
  const {statusProgressWrap: progressWrap} = props;

  const [routines, setRoutines] = React.useState([]);
  const [selectedRoutineRn, setSelectedRoutineRn] = React.useState(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  const loadRoutines = React.useCallback(async () => {
    await progressWrap(async () => {
      const response = await backend.routine.list();
      const list = Array.isArray(response) ? response : (response?.list || []);
      setRoutines(list);
    });
  }, [progressWrap]);

  React.useEffect(() => {
    loadRoutines();
  }, [loadRoutines]);

  const selectedRoutine = React.useMemo(
      () => routines.find((r) => r.routineRn === selectedRoutineRn) || null,
      [routines, selectedRoutineRn],
  );

  React.useEffect(() => {
    const stillExists = routines.some((r) => r.routineRn === selectedRoutineRn);
    if (selectedRoutineRn && routines.length > 0 && !stillExists) {
      setSelectedRoutineRn(null);
    }
  }, [routines, selectedRoutineRn]);

  const handleSelectRoutine = React.useCallback((routine) => {
    setSelectedRoutineRn(routine.routineRn);
  }, []);

  const handleRunNow = React.useCallback(async () => {
    if (!selectedRoutine) return;
    await progressWrap(async () => {
      await backend.routine.run(selectedRoutine.routineRn);
      await loadRoutines();
    });
  }, [loadRoutines, progressWrap, selectedRoutine]);

  const handleToggleStatus = React.useCallback(async () => {
    if (!selectedRoutine) return;
    const newStatus = selectedRoutine.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    await progressWrap(async () => {
      await backend.routine.setStatus(selectedRoutine.routineRn, newStatus);
      await loadRoutines();
    });
  }, [loadRoutines, progressWrap, selectedRoutine]);

  const handleDeleteOpen = React.useCallback(() => setDeleteConfirmOpen(true), []);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!selectedRoutine) return;
    await progressWrap(async () => {
      await backend.routine.delete(selectedRoutine.routineRn);
      setSelectedRoutineRn(null);
      await loadRoutines();
    });
    setDeleteConfirmOpen(false);
  }, [loadRoutines, progressWrap, selectedRoutine]);

  const handleEditOpen = React.useCallback(() => setEditOpen(true), []);
  const handleEditClose = React.useCallback(() => setEditOpen(false), []);

  const handleEditSave = React.useCallback(async (payload) => {
    if (!selectedRoutine) return;
    await progressWrap(async () => {
      await backend.routine.update(selectedRoutine.routineRn, payload);
      await loadRoutines();
    });
    setEditOpen(false);
  }, [loadRoutines, progressWrap, selectedRoutine]);

  return (
    <Stack
      direction={{xs: 'column', md: 'row'}}
      alignItems='stretch'
      sx={{width: '100%', height: {md: 'calc(99vh - 32px)'}}}
    >
      <Box sx={{flexShrink: 0, alignSelf: 'stretch', display: 'flex'}}>
        <RoutinesList
          routines={routines}
          onSelectRoutine={handleSelectRoutine}
          onRefresh={loadRoutines}
          selectedRoutineRn={selectedRoutineRn}
        />
      </Box>
      <Box sx={{flex: 1, display: 'flex', backgroundColor: '#FFFFFF'}}>
        <RoutinesView
          routine={selectedRoutine}
          onRunNow={handleRunNow}
          onEdit={handleEditOpen}
          onToggleStatus={handleToggleStatus}
          onDelete={handleDeleteOpen}
        />
      </Box>
      <RoutinesEdit
        open={editOpen}
        routine={selectedRoutine}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />
      {
        deleteConfirmOpen &&
          <DeleteConfirmDialog
            setDialogOpen={setDeleteConfirmOpen}
            onClickConfirmDelete={handleDeleteConfirm}
          />
      }
    </Stack>
  );
};

RoutinesScene.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, dispatch)(React.memo(RoutinesScene));
